import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Pencil,
  Trophy,
  Users,
  Dices,
  RotateCcw,
  Undo2,
  Download,
  Upload,
  ContactRound,
  Eye,
  UserPlus,
  Search,
  X,
  Check,
  Sun,
  Moon,
  Trash2,
  ChevronDown,
  ChevronRight,
  Gift,
} from 'lucide-react'
import { LayoutGrid } from 'lucide-react'
import { useTheme } from '@/shared/context/ThemeContext'
import {
  useSorteo,
  useParticipantes,
  useGanadores,
  useRegistrarGanador,
  useResetearGanadores,
  useEliminarGanador,
  useDeshacerUltimoGanador,
  useDescargarGanadores,
  useCargarExcel,
  useEliminarParticipante,
  useEliminarParticipantes,
  useCrearParticipante,
} from '../hooks/useSorteos'
import { SlotMachine } from '../components/SlotMachine'
import { CircularRoulette } from '../components/CircularRoulette'
import { WinnersModal } from '../components/WinnersModal'
import { ParticipantsUpload } from '../components/ParticipantsUpload'
import { PremioActualCard } from '../components/PremioActualCard'
import type { Participante, MensajePremio } from '../types/sorteos.types'

export function SorteoDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const { id } = useParams<{ id: string }>()
  const sorteoId = id ?? ''

  const { data: sorteo, isLoading } = useSorteo(sorteoId)
  const { data: participantesData } = useParticipantes(sorteoId)
  const { data: ganadoresData } = useGanadores(sorteoId)

  const registrarGanador = useRegistrarGanador()
  const resetear = useResetearGanadores()
  const eliminarGanadorMutation = useEliminarGanador()
  const deshacerUltimo = useDeshacerUltimoGanador()
  const descargar = useDescargarGanadores()
  const cargarExcel = useCargarExcel()
  const eliminarParticipante = useEliminarParticipante()
  const eliminarParticipantes = useEliminarParticipantes()
  const crearParticipante = useCrearParticipante()

  const [showWinners, setShowWinners] = useState(false)
  const [showAddParticipant, setShowAddParticipant] = useState(false)
  const [newParticipant, setNewParticipant] = useState({ nombre: '', correo: '', pais: '' })
  const [paisFiltro, setPaisFiltro] = useState<string>('')
  const [selectedPremioId, setSelectedPremioId] = useState<string | null>(null)
  const [searchLista, setSearchLista] = useState('')
  const [winnerCelebration, setWinnerCelebration] = useState<{
    nombre: string
    posicion: number
    premio?: MensajePremio
    textoGanado?: string
    premioModel?: import('../types/sorteos.types').Premio
  } | null>(null)
  const [confirmEliminar, setConfirmEliminar] = useState(false)
  const [modoEliminar, setModoEliminar] = useState(false)
  const [listaCollapsed, setListaCollapsed] = useState(true)
  const [premiosCollapsed, setPremiosCollapsed] = useState(true)
  const [showGrid, setShowGrid] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [showDescription, setShowDescription] = useState(false)
  const [celebrationImgError, setCelebrationImgError] = useState(false)
  const [mainImgError, setMainImgError] = useState(false)

  const ganadoresRef = useRef<HTMLDivElement>(null)
  const listaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const lEl = listaRef.current
    if (!lEl) return

    if (listaCollapsed) {
      lEl.style.minHeight = ''
      const scrollDiv = lEl.querySelector('.overflow-y-auto') as HTMLElement
      if (scrollDiv) scrollDiv.style.maxHeight = '16rem'
      return
    }

    const sync = () => {
      const gEl = ganadoresRef.current
      if (!gEl || !lEl) return
      const scrollDiv = lEl.querySelector('.overflow-y-auto') as HTMLElement
      // Reset
      lEl.style.minHeight = ''
      if (scrollDiv) scrollDiv.style.maxHeight = '16rem'
      requestAnimationFrame(() => {
        const gBottom = gEl.getBoundingClientRect().bottom
        const lBottom = lEl.getBoundingClientRect().bottom
        if (gBottom > lBottom) {
          const diff = gBottom - lBottom
          lEl.style.minHeight = `${lEl.offsetHeight + diff}px`
          if (scrollDiv) scrollDiv.style.maxHeight = `${scrollDiv.offsetHeight + diff}px`
        }
      })
    }
    sync()
    const observer = new ResizeObserver(sync)
    if (ganadoresRef.current) observer.observe(ganadoresRef.current)
    return () => observer.disconnect()
  }, [ganadoresData, listaCollapsed])


  if (isLoading || !sorteo) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  const participantes = participantesData?.participantes ?? []
  const ganadores = ganadoresData?.ganadores ?? []

  // Filter participantes by pais if needed
  const filteredParticipantes = paisFiltro
    ? participantes.filter(p => p.pais === paisFiltro)
    : participantes

  // Búsqueda visual en la lista (no afecta el sorteo)
  const displayParticipantes = searchLista
    ? filteredParticipantes.filter(p =>
        p.nombre.toLowerCase().includes(searchLista.toLowerCase()) ||
        (p.correo && p.correo.toLowerCase().includes(searchLista.toLowerCase())) ||
        (p.pais && p.pais.toLowerCase().includes(searchLista.toLowerCase()))
      )
    : filteredParticipantes

  // Calculate giros restantes for slot type
  const ganadoresPais = paisFiltro
    ? ganadores.filter(g => g.pais === paisFiltro).length
    : ganadores.length
  const girosRestantes = sorteo.tipo_ruleta === 'slot'
    ? Math.max(0, sorteo.max_ganadores - ganadoresPais)
    : undefined

  // Get unique countries
  const paises = [...new Set(participantes.map(p => p.pais).filter(Boolean))] as string[]

  const findPremioForPosition = (posicion: number): MensajePremio | undefined => {
    return sorteo.mensajes_premios?.find(p => p.posicion === posicion)
  }

  const findPremiosForPosition = (posicion: number): MensajePremio[] => {
    return sorteo.mensajes_premios?.filter(p => p.posicion === posicion) ?? []
  }

  const handleWinnerSuccess = (participante: Participante, data: import('../types/sorteos.types').GanadorResult) => {
    const premio = findPremioForPosition(data.posicion)
    setCelebrationImgError(false)
    setWinnerCelebration({
      nombre: participante.nombre,
      posicion: data.posicion,
      premio,
      premioModel: data.ganador.premio ?? undefined,
    })
    if (data.premio_agotado) {
      if (data.proximo_premio) {
        setSelectedPremioId(data.proximo_premio.id)
        setTimeout(() => toast.info(`Siguiente premio: ${data.proximo_premio!.nombre}`), 1500)
      } else {
        setSelectedPremioId(null)
        setTimeout(() => toast.success('¡Todos los premios han sido entregados!'), 1500)
      }
    }
  }

  const handleSlotWinner = (participante: Participante) => {
    registrarGanador.mutate(
      { sorteoId, payload: { participante_id: participante.id, premio_id: selectedPremioId ?? undefined } },
      { onSuccess: (data) => handleWinnerSuccess(participante, data) }
    )
  }

  const handleCircularResult = (participante: Participante) => {
    registrarGanador.mutate(
      { sorteoId, payload: { participante_id: participante.id, premio_id: selectedPremioId ?? undefined } },
      { onSuccess: (data) => handleWinnerSuccess(participante, data) }
    )
  }

  const isStandalone = location.pathname.endsWith('/vista')

  return (
    <div className={`relative ${isStandalone ? 'p-6' : ''}`}>
      <div className="relative z-10 space-y-4">
      {/* Header buttons */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="icon" onClick={() => setShowGrid(!showGrid)} className={showGrid ? 'text-teal-500' : ''}>
          <LayoutGrid className="h-4 w-4" />
        </Button>
        {isStandalone && (
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => navigate(`/sorteos/${sorteoId}/editar`)}
        >
          <Pencil className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      {/* Barra participantes activos + acciones */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-2 px-4">
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-500" />
              <span className="text-sm font-medium">Participantes activos:</span>
              <span className="text-lg font-bold text-black dark:text-white">{filteredParticipantes.length}</span>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddParticipant(true)}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Manual
              </Button>
              <Button
                size="sm"
                className="bg-teal-800 hover:bg-teal-900 text-white"
                onClick={() => setShowUpload(true)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Excel
              </Button>
              {filteredParticipantes.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                  onClick={() => setConfirmEliminar(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">
        {/* Left: Ruleta/Slot */}
        <div className="space-y-4">
          {sorteo.tipo_ruleta === 'slot' ? (
            <SlotMachine
              participantes={filteredParticipantes}
              onWinner={handleSlotWinner}
              disabled={!sorteo.activo || registrarGanador.isPending}
              maxGanadores={sorteo.max_ganadores}
              girosRestantes={girosRestantes}
            >
              <div className="flex gap-2 flex-wrap justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetear.mutate({ sorteoId, pais: paisFiltro || undefined })}
                  disabled={resetear.isPending || ganadores.length === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  {resetear.isPending ? 'Reseteando...' : paisFiltro ? `Resetear ${paisFiltro}` : 'Resetear todos'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deshacerUltimo.mutate(sorteoId)}
                  disabled={deshacerUltimo.isPending || ganadores.length === 0}
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  {deshacerUltimo.isPending ? 'Deshaciendo...' : 'Cancelar último'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => descargar.mutate(sorteoId)}
                  disabled={descargar.isPending || ganadores.length === 0}
                >
                  <Download className="h-4 w-4 mr-1" />
                  {descargar.isPending ? 'Descargando...' : 'Descargar Excel'}
                </Button>
              </div>
            </SlotMachine>
          ) : (
            <CircularRoulette
              participantes={filteredParticipantes}
              onResult={handleCircularResult}
              disabled={!sorteo.activo || registrarGanador.isPending}
            >
              <div className="flex gap-2 flex-wrap justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetear.mutate({ sorteoId, pais: paisFiltro || undefined })}
                  disabled={resetear.isPending || ganadores.length === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  {resetear.isPending ? 'Reseteando...' : 'Resetear todos'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deshacerUltimo.mutate(sorteoId)}
                  disabled={deshacerUltimo.isPending || ganadores.length === 0}
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  {deshacerUltimo.isPending ? 'Deshaciendo...' : 'Cancelar último'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => descargar.mutate(sorteoId)}
                  disabled={descargar.isPending || ganadores.length === 0}
                >
                  <Download className="h-4 w-4 mr-1" />
                  {descargar.isPending ? 'Descargando...' : 'Descargar Excel'}
                </Button>
              </div>
            </CircularRoulette>
          )}

          {/* Country filter for slot */}
          {sorteo.tipo_ruleta === 'slot' && sorteo.mostrar_filtro_pais && paises.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardContent className="pt-4">
                <p className="text-sm font-medium mb-2">Filtrar por país</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={paisFiltro === '' ? 'default' : 'outline'}
                    className={paisFiltro === '' ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}
                    onClick={() => setPaisFiltro('')}
                  >
                    Todos
                  </Button>
                  {paises.map(pais => (
                    <Button
                      key={pais}
                      size="sm"
                      variant={paisFiltro === pais ? 'default' : 'outline'}
                      className={paisFiltro === pais ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}
                      onClick={() => setPaisFiltro(pais)}
                    >
                      {pais}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ganadores table */}
          <Card ref={ganadoresRef} className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="h-5 w-5 text-teal-500" />
                Ganadores
                <Badge className="badge-gradient">{ganadores.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ganadores.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Aún no hay ganadores
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                      <TableHead>#</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Premio</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ganadores.map((g) => {
                      const premio = g.texto_ganado || findPremiosForPosition(g.posicion).map(p => p.mensaje).join(', ')
                      return (
                        <TableRow key={g.id}>
                          <TableCell>
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300 font-bold text-xs">
                              {g.posicion}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{g.nombre}</TableCell>
                          <TableCell className="text-teal-600 dark:text-teal-400">{premio || 'sin dato'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => {
                                  setCelebrationImgError(false)
                                  setWinnerCelebration({
                                    nombre: g.nombre,
                                    posicion: g.posicion,
                                    premio: findPremioForPosition(g.posicion),
                                    textoGanado: g.texto_ganado ?? undefined,
                                    premioModel: g.premio ?? undefined,
                                  })
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                disabled={eliminarGanadorMutation.isPending}
                                onClick={() => eliminarGanadorMutation.mutate({ sorteoId, ganadorId: g.id })}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Info + Participantes */}
        <div className="space-y-4">
          {/* Card título + imagen */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="pt-4 pb-4 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                <Dices className="h-6 w-6 text-black dark:text-white" />
                <h1 className="text-2xl font-bold">{sorteo.nombre}</h1>
              </div>
              {sorteo.imagen && (
                !mainImgError ? (
                  <img
                    src={sorteo.imagen}
                    alt={sorteo.nombre}
                    className="max-h-56 rounded-lg object-contain"
                    onError={() => setMainImgError(true)}
                  />
                ) : (
                  <div className="flex items-center justify-center h-40 w-40 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <Trophy className="h-12 w-12 text-teal-500" />
                  </div>
                )
              )}
            </CardContent>
          </Card>

          {/* Premio activo (sistema por cantidad) */}
          <PremioActualCard
            sorteoId={sorteoId}
            selectedPremioId={selectedPremioId}
            onSelectPremio={setSelectedPremioId}
          />

          {/* Premios legacy (mensajes_premios) */}
          {sorteo.mensajes_premios && sorteo.mensajes_premios.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="py-3">
                <button
                  onClick={() => setPremiosCollapsed(!premiosCollapsed)}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  {premiosCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <Gift className="h-5 w-5 text-teal-500 shrink-0" />
                  <span className="text-base font-semibold shrink-0">Premios</span>
                </button>
              </CardHeader>
              {!premiosCollapsed && (
                <CardContent>
                  <div className="space-y-1.5">
                    {[...sorteo.mensajes_premios]
                      .sort((a, b) => a.posicion - b.posicion)
                      .map((p, i) => {
                        const entregado = ganadores.some(g => g.posicion === p.posicion)
                        return (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300 font-bold text-[10px] shrink-0">
                              {p.posicion}
                            </span>
                            <span className={`text-sm whitespace-pre-line ${entregado ? 'line-through text-muted-foreground' : ''}`}>{p.mensaje}</span>
                            {entregado && <Check className="h-4 w-4 text-teal-500 shrink-0" />}
                          </div>
                        )
                      })}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Lista de participantes */}
          <Card ref={listaRef} className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setListaCollapsed(!listaCollapsed)}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  {listaCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <ContactRound className="h-5 w-5 text-teal-500 shrink-0" />
                  <span className="text-base font-semibold shrink-0">Lista de participantes</span>
                </button>
                {!listaCollapsed && participantes.length > 0 && (
                  <>
                    <div className="relative flex-1 ml-2">
                      <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Buscar..."
                        value={searchLista}
                        onChange={(e) => setSearchLista(e.target.value)}
                        className="h-7 pl-7 text-sm"
                      />
                    </div>
                    {searchLista && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setSearchLista('')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-7 w-7 shrink-0 ${modoEliminar ? 'text-red-500 bg-red-50 dark:bg-red-950/30' : 'text-muted-foreground'}`}
                      onClick={() => setModoEliminar(!modoEliminar)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            {!listaCollapsed && (
            <CardContent>
              {participantes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Agrega participantes
                </p>
              ) : (
              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                      <TableHead>Nombre</TableHead>
                      <TableHead>Contacto</TableHead>
                      {sorteo.mostrar_filtro_pais && <TableHead>País</TableHead>}
                      {modoEliminar && <TableHead className="w-10" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayParticipantes.slice(0, 50).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.nombre}</TableCell>
                        <TableCell className="text-muted-foreground">{p.correo || 'sin dato'}</TableCell>
                        {sorteo.mostrar_filtro_pais && (
                          <TableCell className="text-muted-foreground">{p.pais || 'sin dato'}</TableCell>
                        )}
                        {modoEliminar && (
                          <TableCell className="p-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => eliminarParticipante.mutate({ sorteoId, participanteId: p.id })}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {displayParticipantes.length > 50 && (
                      <TableRow>
                        <TableCell colSpan={(sorteo.mostrar_filtro_pais ? 3 : 2) + (modoEliminar ? 1 : 0)} className="text-center text-muted-foreground">
                          ... y {displayParticipantes.length - 50} más
                        </TableCell>
                      </TableRow>
                    )}
                    {searchLista && displayParticipantes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={(sorteo.mostrar_filtro_pais ? 3 : 2) + (modoEliminar ? 1 : 0)} className="text-center text-muted-foreground py-4">
                          Sin resultados
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              )}
            </CardContent>
            )}
          </Card>

        </div>
      </div>

      {/* Add Participant Dialog */}
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-4 py-2">
            <h3 className="text-lg font-semibold text-black dark:text-white">Agregar participante</h3>
            <div>
              <Label htmlFor="part-nombre">Nombre *</Label>
              <Input
                id="part-nombre"
                value={newParticipant.nombre}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Nombre del participante"
              />
            </div>
            <div>
              <Label htmlFor="part-correo">Correo</Label>
              <Input
                id="part-correo"
                value={newParticipant.correo}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, correo: e.target.value }))}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <Label htmlFor="part-pais">País</Label>
              <Input
                id="part-pais"
                value={newParticipant.pais}
                onChange={(e) => setNewParticipant(prev => ({ ...prev, pais: e.target.value }))}
                placeholder="País"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddParticipant(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-teal-800 hover:bg-teal-900 text-white"
                disabled={!newParticipant.nombre.trim() || crearParticipante.isPending}
                onClick={() => {
                  crearParticipante.mutate({
                    sorteoId,
                    payload: {
                      nombre: newParticipant.nombre.trim(),
                      correo: newParticipant.correo.trim() || undefined,
                      pais: newParticipant.pais.trim() || undefined,
                    },
                  }, {
                    onSuccess: () => {
                      setNewParticipant({ nombre: '', correo: '', pais: '' })
                      setShowAddParticipant(false)
                    },
                  })
                }}
              >
                {crearParticipante.isPending ? 'Agregando...' : 'Agregar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={showDescription} onOpenChange={setShowDescription}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-3 py-2">
            <h3 className="text-lg font-semibold text-black dark:text-white">{sorteo.nombre}</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{sorteo.descripcion}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <div className="py-2">
            <ParticipantsUpload
              onUpload={(file) => {
                cargarExcel.mutate({ sorteoId, archivo: file })
                setShowUpload(false)
              }}
              onDelete={() => {
                eliminarParticipantes.mutate(sorteoId)
                setShowUpload(false)
              }}
              isUploading={cargarExcel.isPending}
              isDeleting={eliminarParticipantes.isPending}
              totalParticipantes={participantes.length}
              camposExcel={sorteo.campos_excel}
              hideCard
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Winner Celebration */}
      <Dialog open={!!winnerCelebration} onOpenChange={() => setWinnerCelebration(null)}>
        <DialogContent className="sm:max-w-md text-center">
          <div className="flex flex-col items-center gap-4 py-4">
            {(winnerCelebration?.premioModel?.imagen_url || winnerCelebration?.premio?.imagen_url) && !celebrationImgError ? (
              <img
                src={winnerCelebration.premioModel?.imagen_url ?? winnerCelebration.premio?.imagen_url ?? ''}
                alt="Premio"
                className="max-h-40 rounded-lg object-contain"
                onError={() => setCelebrationImgError(true)}
              />
            ) : (
              <Trophy className="h-16 w-16 text-teal-500" />
            )}
            <div className="space-y-2">
              <p className="text-2xl font-bold text-black dark:text-white">
                {winnerCelebration?.nombre}
              </p>
              <p className="text-sm text-muted-foreground">
                Ganador #{winnerCelebration?.posicion}
              </p>
              {/* Premio del nuevo sistema (por cantidad) */}
              {winnerCelebration?.premioModel && (
                <p className="text-lg text-teal-700 dark:text-teal-300 font-medium">
                  {winnerCelebration.premioModel.nombre}
                </p>
              )}
              {/* Premio legacy (mensajes_premios) */}
              {!winnerCelebration?.premioModel && winnerCelebration?.posicion && findPremiosForPosition(winnerCelebration.posicion).length > 0 && (
                <div className="space-y-1">
                  {findPremiosForPosition(winnerCelebration.posicion).map((p, i) => (
                    <p key={i} className="text-lg text-teal-700 dark:text-teal-300 font-medium whitespace-pre-line">
                      {p.mensaje}
                    </p>
                  ))}
                  {findPremiosForPosition(winnerCelebration.posicion)[0]?.mensaje_ganador && (
                    <p className="text-sm text-muted-foreground italic">
                      {findPremiosForPosition(winnerCelebration.posicion)[0].mensaje_ganador}
                    </p>
                  )}
                </div>
              )}
              {winnerCelebration?.textoGanado && !winnerCelebration?.premioModel && !findPremiosForPosition(winnerCelebration.posicion ?? 0).length && (
                <p className="text-lg text-teal-700 dark:text-teal-300 font-medium">
                  Premio: {winnerCelebration.textoGanado}
                </p>
              )}
            </div>
            <Button
              className="bg-teal-800 hover:bg-teal-900 text-white mt-2"
              onClick={() => setWinnerCelebration(null)}
            >
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Winners Modal */}
      <WinnersModal
        open={showWinners}
        onOpenChange={setShowWinners}
        ganadores={ganadores}
        onDescargar={() => descargar.mutate(sorteoId)}
        sorteoNombre={sorteo.nombre}
      />

      {/* Confirmar eliminar participantes */}
      <Dialog open={confirmEliminar} onOpenChange={setConfirmEliminar}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/30">
              <Trash2 className="h-6 w-6 text-red-500" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-lg">Eliminar participantes</p>
              <p className="text-sm text-muted-foreground mt-1">
                ¿Estás seguro de eliminar todos los participantes? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmEliminar(false)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={() => {
                  eliminarParticipantes.mutate(sorteoId)
                  setConfirmEliminar(false)
                }}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showGrid && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {Array.from({ length: 21 }, (_, i) => {
            const pct = i * 5
            return (
              <div key={`v${i}`} className="absolute top-0 bottom-0" style={{ left: `${pct}%` }}>
                <div className="w-px h-full bg-red-500/30" />
                <span className="absolute top-1 text-[9px] font-mono text-red-500 bg-white/80 dark:bg-black/80 px-0.5 rounded">{pct}</span>
              </div>
            )
          })}
          {Array.from({ length: 41 }, (_, i) => {
            const px = i * 50
            return (
              <div key={`h${i}`} className="absolute left-0 right-0" style={{ top: `${px}px` }}>
                <div className="h-px w-full bg-blue-500/30" />
                <span className="absolute left-1 text-[9px] font-mono text-blue-500 bg-white/80 dark:bg-black/80 px-0.5 rounded">{px}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
    </div>
  )
}
